import { useCallback, useEffect } from 'react';
import { SimpleGrid, Stack } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import i18n from 'i18next';

import { AppConstants, RouteConstants, ServiceConstants } from '../../../constants';
import { AppUtils, StorageUtils } from 'utils';
import { CalculatorProps, CreditRequestBasicForm } from 'types';
import CreditRequestPersonalInfo from './personal-info';
import { IRootState } from 'config/redux';
import { PublicService } from 'services';
import { CalculatorForm, SectionText, SectionTitle } from 'components';
import { useCreditRequestForm } from 'context';

const {
  CREDIT_AMOUNT_MIN,
  CREDIT_AMOUNT_MAX,
  CREDIT_PAYMENT_MIN,
  CREDIT_PAYMENT_MAX,
  CREDIT_REQUEST_STEP_FORM,
  CREDIT_REQUEST_STEP_BASIC,
  MIN_TIME,
  MAX_TIME,
  PHONE_NUMBER_CODES,
  PHONE_NUMBER_DATA,
  STORAGE_TEMP_CALCULATOR_DETAILS,
  PHONE_NUMBER_CODE_SWITZERLAND
} = AppConstants;

const { ROUTE_CREDIT_REQUEST_FORM } = RouteConstants;
const { REQUEST_LOGIN_ID } = ServiceConstants;

const CreditRequestBasicPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { form, options, setActiveStep } = useCreditRequestForm();
  const loginServiceObject = useSelector((state: IRootState) => state.service.object[REQUEST_LOGIN_ID]);

  useEffect(() => {
    const details: CalculatorProps | undefined = StorageUtils.getObject(STORAGE_TEMP_CALCULATOR_DETAILS);
    if (details) {
      Object.keys(details).forEach((el) =>
        form.setFieldValue(el, details[el as keyof CalculatorProps])
      );
      StorageUtils.deleteKey(STORAGE_TEMP_CALCULATOR_DETAILS);
    }
  }, []);

  useEffect(() => {
    AppUtils.resolveServiceObject(
      loginServiceObject,
      REQUEST_LOGIN_ID,
      () => {
        setActiveStep(CREDIT_REQUEST_STEP_FORM);
        navigate(ROUTE_CREDIT_REQUEST_FORM, {
          state: {
            initialCreditData: {
              creditAmount: form.values.creditAmount,
              paymentPeriod: form.values.paymentPeriod,
              monthlyInstallmentFrom: form.values.monthlyInstallmentFrom,
              monthlyInstallmentTo: form.values.monthlyInstallmentTo,
              workdayAvailabilityFrom: MIN_TIME,
              workdayAvailabilityTo: MAX_TIME,
              saturdayAvailabilityFrom: MIN_TIME,
              saturdayAvailabilityTo: MAX_TIME,
              partnerWorkdayAvailabilityFrom: MIN_TIME,
              partnerWorkdayAvailabilityTo: MAX_TIME,
              partnerSaturdayAvailabilityFrom: MIN_TIME,
              partnerSaturdayAvailabilityTo: MAX_TIME,
            },
            options
          }
        });
      },
      () => form.setFieldError('email', t('login.error.credentials'))
    );
  }, [loginServiceObject]);

  const checkApplication = useCallback((values: CreditRequestBasicForm) => {
    if (!isFormValid(values)) {
      return;
    }

    const languageFromI18n = i18n.language.toUpperCase().split('-')[0] as 'EN' | 'DE' | 'SR' | 'AL' | 'ES' | 'FR' | 'IT' | 'PT' | 'TR'
    const countryCode = PHONE_NUMBER_CODES.find(code => values.phoneNumber.startsWith(code));

    PublicService.login({
      email: values.email,
      countryCode: countryCode!,
      phoneNumber: values.phoneNumber.slice(countryCode!.length),
      firstName: values.firstName,
      lastName: values.lastName,
      language: languageFromI18n
    });
  }, [i18n]);

  const isFormValid = (values: any) => {
    const validations: {
      field: string;
      condition: boolean;
      error: string
    }[] = [
        { field: 'creditAmount', condition: !values.creditAmount || values.creditAmount <= 0, error: t('input.creditAmount.error.required') },
        { field: 'creditAmount', condition: values.creditAmount < CREDIT_AMOUNT_MIN, error: t('input.creditAmount.error.min', { amount: CREDIT_AMOUNT_MIN }) },
        { field: 'creditAmount', condition: values.creditAmount > CREDIT_AMOUNT_MAX, error: t('input.creditAmount.error.max', { amount: CREDIT_AMOUNT_MAX }) },
        { field: 'paymentPeriod', condition: !values.paymentPeriod || values.paymentPeriod <= 0, error: t('input.paymentPeriod.error.required') },
        { field: 'paymentPeriod', condition: values.paymentPeriod < CREDIT_PAYMENT_MIN, error: t('input.paymentPeriod.error.min', { amount: CREDIT_PAYMENT_MIN }) },
        { field: 'paymentPeriod', condition: values.paymentPeriod > CREDIT_PAYMENT_MAX, error: t('input.paymentPeriod.error.max', { amount: CREDIT_PAYMENT_MAX }) },
        { field: 'firstName', condition: !values.firstName, error: t('input.required') },
        { field: 'lastName', condition: !values.lastName, error: t('input.required') },
        { field: 'phoneNumber', condition: !values.phoneNumber, error: t('input.required') },
        { field: 'phoneNumber', condition: !PHONE_NUMBER_CODES.some(code => values.phoneNumber.startsWith(code) && PHONE_NUMBER_DATA[code]?.length.includes(values.phoneNumber.replace(/\s/g, '').length)), error: t('input.phoneNumber.invalid') },
        { field: 'phoneNumber', condition: values.phoneNumber.startsWith(PHONE_NUMBER_CODE_SWITZERLAND) && !values.phoneNumber.startsWith(PHONE_NUMBER_CODE_SWITZERLAND + ' 7') && values.phoneNumber.length > 3, error: t('input.phoneNumber.swissStartWith') },
        { field: 'email', condition: !values.email, error: t('input.required') },
        { field: 'email', condition: values.email && !/^\S+@\S+$/.test(values.email), error: t('input.email.invalid') },
      ];

    let isValid = true;
    validations.forEach(({ field, condition, error }) => {
      if (condition) {
        form.setFieldError(field, error);
        isValid = false;
      }
    });

    return isValid;
  }

  return (
    <>
      <Stack gap={10} mb={40} maw={900}>
        <SectionTitle title={t(`credit.step.${CREDIT_REQUEST_STEP_BASIC}.title`)} variant={'dark'} pt={'xl'} />
        <SectionText variant='dark' text={t(`credit.step.${CREDIT_REQUEST_STEP_BASIC}.description`)} />
      </Stack>
      <form onSubmit={form.onSubmit((values) => checkApplication(values))}>
        <SimpleGrid cols={{ base: 1, md: 2 }}>
          <CalculatorForm
            form={form}
            options={options!}
            t={t}
            sectionVariant={'green'}
            labelVariant={'dark'}
            inputVariant={'light'}
            disclaimerVariant={'dark'}
            sliderColor={'#2F6F16'}
            variant={'light'}
          />
          <CreditRequestPersonalInfo t={t} form={form} />
        </SimpleGrid>
      </form>
    </>
  );
}

export default CreditRequestBasicPage;